import type { ReactNode } from 'react';

interface SectionProps {
  id: string;
  label: string;
  title: ReactNode;
  intro?: ReactNode;
  children?: ReactNode;
}

/**
 * One composition for every section below the fold: micro-label, serif heading,
 * an optional standfirst on the right, a hairline, then the content.
 */
export function Section({ id, label, title, intro, children }: SectionProps) {
  // The backdrop video runs behind the whole page. The plate is confined to the
  // content column rather than stretched full width, so the footage stays
  // visible down both margins while the type keeps a readable ground under it —
  // the same frosted card the hero panel uses.
  return (
    <section id={id} className="relative z-10 w-full px-4 sm:px-6 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-5xl bg-paper/90 dark:bg-paper/85 backdrop-blur-sm border border-hairline shadow-sm px-5 sm:px-8 md:px-12 py-16 sm:py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
              {label}
            </div>
            <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl font-serif font-normal leading-tight tracking-tight text-ink">
              {title}
            </h2>
          </div>
          {intro ? (
            <div className="flex md:items-end">
              <p className="text-sm md:text-[15px] text-ink/70 leading-relaxed">{intro}</p>
            </div>
          ) : null}
        </div>

        {children ? (
          <>
            <div className="mt-6 sm:mt-8 md:mt-10 h-px bg-hairline w-full" />
            <div className="mt-8 sm:mt-10 md:mt-12">{children}</div>
          </>
        ) : null}
      </div>
    </section>
  );
}

export default Section;
